'use client';

import { cn } from '../lib/utils';

interface SectionHeaderProps {
  /** Title content - can be a string or ReactNode with TitleHighlight components */
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  icon?: React.ComponentType<{ className?: string; style?: React.CSSProperties }>;
  /** @deprecated Use variant="watermark" instead */
  iconOffset?: string;
  /** @deprecated Use variant="watermark" instead */
  iconSize?: string;
  /** @deprecated Use variant="watermark" instead */
  mobileIconSize?: string;
  /** Display variant: "inline" shows icon with connecting line, "watermark"
   *  shows large background icon, "plain" is just the title + subtitle (no
   *  connecting line, no icon). */
  variant?: 'inline' | 'watermark' | 'plain';
  as?: 'h1' | 'h2';
  actions?: React.ReactNode;
  className?: string;
}

/**
 * The watermark sits in a layer spanning the FULL header box, and this mask
 * is applied to that layer — so the fade is box-relative: it always completes
 * just before the wrapper's `overflow-hidden` clip line, no matter how tall
 * or short a given header is (with/without subtitle, any padding override).
 * Masking the icon itself doesn't work: the icon is fixed-size, the box
 * isn't, so the clip line can land anywhere in the icon's own gradient.
 */
const WATERMARK_LAYER_FADE: React.CSSProperties = {
  WebkitMaskImage:
    'linear-gradient(to bottom, transparent 0%, black 22%, black 55%, transparent 94%)',
  maskImage: 'linear-gradient(to bottom, transparent 0%, black 22%, black 55%, transparent 94%)',
};

export function SectionHeader({
  title,
  subtitle,
  icon: Icon,
  iconOffset = '4rem',
  iconSize = '160px',
  mobileIconSize = '80px',
  variant = 'inline',
  as: Heading = 'h2',
  actions,
  className,
}: SectionHeaderProps) {
  // Plain variant: title + subtitle only — no connecting line, no icon.
  if (variant === 'plain') {
    return (
      <div className={cn('relative mb-6 px-2 md:mb-10 md:px-0', className)}>
        <div className={cn('relative', actions && 'flex items-start gap-3')}>
          <div className="min-w-0 flex-1">
            <Heading className="text-foreground text-2xl font-bold tracking-tighter uppercase md:text-4xl lg:text-5xl">
              {title}
            </Heading>
            {subtitle && (
              <div className="text-muted-foreground mt-1 text-xs font-normal tracking-widest uppercase md:text-sm">
                {subtitle}
              </div>
            )}
          </div>
          {actions}
        </div>
      </div>
    );
  }

  // Inline variant: icon with connecting line
  if (variant === 'inline') {
    return (
      <div className={cn('relative mb-6 px-2 md:mb-10 md:px-0', className)}>
        <div className={cn('relative', actions && 'flex items-start gap-3')}>
          <div className="min-w-0 flex-1">
            {/* Title row with line and icon */}
            <div className="flex items-center gap-3">
              <Heading className="text-foreground text-2xl font-bold tracking-tighter uppercase md:text-4xl lg:text-5xl">
                {title}
              </Heading>
              {/* Connecting line */}
              <div className="bg-foreground/20 h-px flex-1" />
              {/* Inline icon */}
              {Icon && <Icon className="text-foreground h-5 w-5 flex-shrink-0 md:h-6 md:w-6" />}
            </div>
            {subtitle && (
              <div className="text-muted-foreground mt-1 text-xs font-normal tracking-widest uppercase md:text-sm">
                {subtitle}
              </div>
            )}
          </div>
          {actions}
        </div>
      </div>
    );
  }

  // Watermark variant: large background icon (original behavior).
  //
  // Stacking model:
  //   - Outer wrapper is non-positioned so it sits in its parent's
  //     group 3 (non-positioned in-flow block). Later-in-flow siblings
  //     (cards, sections) are also in group 3 and paint in source order
  //     after the header.
  //   - Outer wrapper still sets `isolation: isolate` to create its own
  //     stacking context. This keeps the watermark's negative z-index
  //     scoped inside the header — no bubbling up past `main` where a
  //     background could hide it.
  //   - Inner row is `position: relative` to give the absolutely-
  //     positioned watermarks a coordinate system; it is NOT a stacking
  //     context (no z-index, no isolation), so the watermark's z:-1 joins
  //     the outer wrapper's stacking context directly.
  return (
    <div
      // The watermark must never paint over (or under) sibling content. The
      // old approach — bottom MARGIN as "room" for the icon to bleed into —
      // couldn't guarantee that: `isolation: isolate` scopes the icon's z:-1
      // (so page backgrounds can't hide it) but also hoists the whole header
      // into the positioned paint phase, ABOVE later siblings' non-positioned
      // text/lines. Instead the bleed room is bottom PADDING inside the box
      // and `overflow-hidden` clips the icon at the boundary — by
      // construction it can no longer overlap anything outside the header.
      // Pages wanting tighter spacing override padding via `className`
      // (tailwind-merge resolves the conflict); the icon just crops sooner.
      className={cn('relative overflow-hidden px-2 pb-6 md:px-0 md:pb-12', className)}
      style={{ isolation: 'isolate' }}
    >
      {/* Watermark layer — spans the whole header box; the box-relative mask
          fades the icon out before the clip line in every direction it could
          meet an edge. zIndex -1 keeps it behind the title within this
          isolated stacking context. */}
      {Icon && (
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0"
          style={{ zIndex: -1, ...WATERMARK_LAYER_FADE }}
        >
          <Icon
            className="text-watermark absolute top-1/2 right-0 -translate-y-1/2 md:hidden"
            style={{ width: mobileIconSize, height: mobileIconSize }}
          />
          <Icon
            className="text-watermark absolute top-0 right-0 hidden md:block"
            style={{ width: iconSize, height: iconSize }}
          />
        </div>
      )}
      <div className={cn('relative', actions && 'flex items-start gap-3')}>
        <div className="min-w-0 flex-1">
          <Heading className="text-foreground relative z-10 text-xl font-bold tracking-tighter uppercase sm:text-2xl md:text-4xl lg:text-5xl">
            {title}
          </Heading>
          {subtitle && (
            <div className="text-muted-foreground relative z-10 mt-1 text-xs font-normal tracking-widest uppercase sm:pl-6 sm:text-sm md:text-base">
              {subtitle}
            </div>
          )}
        </div>
        {actions}
      </div>
    </div>
  );
}
